import type {GraphDataPoint} from '@src/types/statistics.ts';

export const LAST_CONFIGURATION_ID_LOCAL_STORAGE_KEY =
  'pl-analyze-last-configuration-id';
export const PROMOTE_AUTOMATIC_ZOOM_TO_COLLECTION_LOCAL_STORAGE_KEY =
  'pl-promote-automatic-zoom-to-collection';

export const API_VERSION = {
  v1: 'v1',
  v2: 'v2',
} as const;
export type ApiVersionType = (typeof API_VERSION)[keyof typeof API_VERSION];

export const API_PROCESS_FILE_IDENTIFIER_TYPES = {
  default: 'default',
  superResRgb: 'rgb',
  superResConfidence: 'confidence',
} as const;
export type ApiProcessFileIdentifierType =
  (typeof API_PROCESS_FILE_IDENTIFIER_TYPES)[keyof typeof API_PROCESS_FILE_IDENTIFIER_TYPES];

export const API_PROCESS_OUTPUT_FORMATS = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  tiff: 'image/tiff',
} as const;
export type ApiProcessOutputFormatType =
  (typeof API_PROCESS_OUTPUT_FORMATS)[keyof typeof API_PROCESS_OUTPUT_FORMATS];

export const MOSAICKING_ORDER = {
  mostRecent: 'mostRecent',
  leastRecent: 'leastRecent',
  leastCC: 'leastCC',
} as const;
export type MosaickingOrderValue =
  (typeof MOSAICKING_ORDER)[keyof typeof MOSAICKING_ORDER];

// Process API supports also "lanczos" and "box", but the resampling is expected to change.
// So, let's keep the same options as in older apps.
// see https://hello.planet.com/code/sentinel-hub/sentinel-frontend/browser/-/issues/178#note_5722381
export const INTERPOLATOR = {
  bilinear: 'BILINEAR',
  bicubic: 'BICUBIC',
  nearest: 'NEAREST',
} as const;
export type InterpolatorValue =
  (typeof INTERPOLATOR)[keyof typeof INTERPOLATOR];

export const SUPER_RES_LAYER_ID = 'super-res';
export const SUPER_RES_EVALSCRIPT_ID = '52b4a57a-4806-4088-a4c3-887027a32d9d';
export const SUPER_RES_RESOLUTION = 2;
// Calculated based on optimal (waiting time and reliability) pixel count for super-res process request
export const SUPER_RES_AOI_AREA_RANGE = {
  min: 500_000,
  max: 10_000_000,
} as const;
export const SUPER_RES_AOI_OPTIMAL_AREA = 1_500_000;
export const MAX_PROCESS_API_SIZE_PER_SIDE = 2500;

export const PreviewModeString = {
  detail: 'DETAIL',
  preview: 'PREVIEW',
  extendedPreview: 'EXTENDED_PREVIEW',
} as const;
export type PreviewModeStringValue =
  (typeof PreviewModeString)[keyof typeof PreviewModeString];

type ResolutionLimits = {
  resolution: number;
  resolutionCeiling: number;
};

export const DEFAULT_RESOLUTION_CEILING = 2000;
export const STATS_COLLECTION_RESOLUTION_CONFIG: Record<
  string,
  ResolutionLimits
> = {
  // Public datasets
  'sentinel-2-l1c': {
    resolution: 10,
    resolutionCeiling: DEFAULT_RESOLUTION_CEILING,
  },
  'sentinel-2-l2a': {resolution: 10, resolutionCeiling: 1400},
  'aws-lot-l1': {resolution: 15, resolutionCeiling: 1490},
  'aws-lot-l2': {resolution: 30, resolutionCeiling: 1490},
  // BYOC - Default conservative settings
  byoc: {resolution: 0.5, resolutionCeiling: DEFAULT_RESOLUTION_CEILING},
};
export const STATS_EVALSCRIPT_OUTPUT = 'eobrowserStats';

export const STATS_CSV_EXPORT_FIELDS: (keyof GraphDataPoint)[] = [
  'date',
  'min',
  'max',
  'mean',
  'stDev',
  'sampleCount',
  'noDataCount',
  'median',
  'p10',
  'p90',
  'cloudCoveragePercent',
];

export const COLLECTION_MIN_RESOLUTION: Record<string, number> = {
  'sentinel-1-grd': 1500,
  'sentinel-2-l1c': 200,
  'sentinel-2-l2a': 1500,
  'landsat-ot-l1': 1500,
  'landsat-ot-l2': 1500,
  hls: 1500,
};
